import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Globe, Search, BookOpen, Bug, ImageIcon, FileText, CheckCircle2 } from "lucide-react";
import type { ImageGenTabProps } from "./types";

export function ResearchTab(props: ImageGenTabProps) {
  const {
    webSearchEnabled, setWebSearchEnabled, webSearchKeywords, setWebSearchKeywords,
    codexSearchEnabled, setCodexSearchEnabled, codexSearchKeywords, setCodexSearchKeywords,
    codexPrompt, setCodexPrompt, briefLoading, hasCachedBrief,
    fetchBrief, searchProgress, searchQueriesUsed, codexSnippets,
    isAdminUser, debugData, setShowDebugDialog,
    briefText, webReferenceImages, webReferenceThumbnails, codexEntityImages,
    setActiveTab,
  } = props;

  return (
    <div className="space-y-4">
      {/* Web Search */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox id="web-search" checked={webSearchEnabled} onCheckedChange={(c) => setWebSearchEnabled(c === true)} disabled={briefLoading} />
          <label htmlFor="web-search" className="text-xs text-foreground flex items-center gap-1.5 cursor-pointer font-medium">
            <Globe className="w-3.5 h-3.5 text-primary" /> Search internet
          </label>
        </div>
        {webSearchEnabled && (
          <div className="pl-6 space-y-1.5">
            <Input value={webSearchKeywords} onChange={(e) => setWebSearchKeywords(e.target.value)} placeholder="e.g. dark elf ranger, fantasy portrait" className="h-8 text-xs" disabled={briefLoading} />
            <p className="text-[10px] text-muted-foreground/60">Keywords for image search</p>
          </div>
        )}
      </div>

      {/* Codex Search */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox id="codex-search" checked={codexSearchEnabled} onCheckedChange={(c) => setCodexSearchEnabled(c === true)} disabled={briefLoading} />
          <label htmlFor="codex-search" className="text-xs text-foreground flex items-center gap-1.5 cursor-pointer font-medium">
            <BookOpen className="w-3.5 h-3.5 text-primary" /> Search Codex
          </label>
        </div>
        {codexSearchEnabled && (
          <div className="pl-6 space-y-2">
            <Input value={codexSearchKeywords} onChange={(e) => setCodexSearchKeywords(e.target.value)} placeholder="character name, faction..." className="h-8 text-xs" disabled={briefLoading} />
            <Textarea value={codexPrompt} onChange={(e) => setCodexPrompt(e.target.value)} placeholder="Focus on their battle armor..." className="min-h-[40px] text-xs resize-y" rows={2} disabled={briefLoading} />
          </div>
        )}
      </div>

      {/* Search / Create Brief button */}
      <Button variant="default" size="sm" className="w-full gap-1.5" onClick={() => fetchBrief(webSearchEnabled)} disabled={briefLoading}>
        {briefLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
        {hasCachedBrief ? "Re-research" : (webSearchEnabled || codexSearchEnabled ? "Search & Create Brief" : "Create Visual Brief")}
      </Button>

      {/* Brief loading progress */}
      {briefLoading && (
        <div className="flex flex-col gap-1.5 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
            <span className="text-xs text-foreground">
              {searchProgress?.step === "codex_search" ? "Searching codex..." : searchProgress?.detail || "Analyzing..."}
            </span>
          </div>
          {searchProgress?.provider && (
            <div className="flex items-center gap-1.5 ml-6">
              <span className="text-[10px] font-medium text-primary/80">{searchProgress.provider}</span>
              {searchProgress.query && <span className="text-[10px] text-muted-foreground/70 truncate">→ "{searchProgress.query}"</span>}
            </div>
          )}
        </div>
      )}

      {/* Search more */}
      {webSearchEnabled && hasCachedBrief && !briefLoading && (
        <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs h-7" onClick={() => fetchBrief(true, true)}>
          <Search className="w-3 h-3" /> Search More
          {searchQueriesUsed.length > 0 && <span className="text-muted-foreground/60">({searchQueriesUsed.length} done)</span>}
        </Button>
      )}

      {codexSnippets.length > 0 && (
        <div className="p-2 rounded-lg bg-muted/20 border border-border/50">
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <BookOpen className="w-3 h-3 text-primary/60" />
            {codexSnippets.length} lore snippet{codexSnippets.length > 1 ? "s" : ""} enriched the brief
          </p>
        </div>
      )}

      {/* Research results */}
      {!briefLoading && hasCachedBrief && (briefText || webReferenceImages.length > 0 || codexEntityImages.length > 0) && (
        <div className="space-y-3 pt-2 border-t border-border/50">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Research results
          </div>

          {briefText && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Visual brief ({briefText.length} chars)
                </label>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => setActiveTab("art")}>
                  Open in Art Direction →
                </Button>
              </div>
              <div className="p-2 rounded-md bg-muted/20 border border-border/50 max-h-32 overflow-y-auto">
                <p className="text-[11px] text-foreground/80 whitespace-pre-wrap leading-relaxed">
                  {briefText.length > 600 ? briefText.slice(0, 600) + "…" : briefText}
                </p>
              </div>
            </div>
          )}

          {(webReferenceImages.length > 0 || codexEntityImages.length > 0) && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  {webReferenceImages.length + codexEntityImages.length} reference image{webReferenceImages.length + codexEntityImages.length > 1 ? "s" : ""} found
                </label>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => setActiveTab("references")}>
                  Select in References →
                </Button>
              </div>
              <div className="grid grid-cols-5 gap-1">
                {[...webReferenceImages.map((url, i) => ({ url, thumb: webReferenceThumbnails[i] || url, kind: "web" as const })),
                  ...codexEntityImages.map((url) => ({ url, thumb: url, kind: "codex" as const }))]
                  .slice(0, 10)
                  .map((img, i) => (
                    <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-border/50 bg-muted/20">
                      <img
                        src={img.thumb}
                        alt={`Reference ${i + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                      {img.kind === "codex" && (
                        <div className="absolute bottom-0.5 left-0.5 bg-primary/80 rounded-sm p-0.5">
                          <BookOpen className="w-2 h-2 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isAdminUser && debugData && (
        <Button variant="ghost" size="sm" className="w-full h-6 text-[10px] gap-1 text-muted-foreground/50" onClick={() => setShowDebugDialog(true)}>
          <Bug className="w-3 h-3" /> Debug Log
        </Button>
      )}
    </div>
  );
}
